import { useEffect, useState } from 'react';
import {
    Alert, Box, Chip, IconButton, List, ListItemButton, ListItemText, Skeleton, Step, StepLabel,
    Stepper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { LeadFormBuilder } from './lead-form-builder';
import MDButton from '@components/MDButton';
import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import {
    LeadFormsConnection, LeadFormsService, MetaForm, MetaFormField, MetaPage,
} from '../services/lead-forms.service';

interface Props {
    /// Conexão já lida pela aba; a conta da Meta é a do grupo, conectada no cabeçalho.
    connection: LeadFormsConnection;
    onClose: () => void;
    onSaved: () => void;
}

const STEPS = ['pagina', 'formulario', 'campos', 'concluido'];

export const LeadFormsWizard = ({ connection, onClose, onSaved }: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert, alertDelete } = useAlert();
    const service = new LeadFormsService();

    // Quem já configurou quer ver os formulários da página escolhida, não escolhê-la de novo.
    const [step, setStep] = useState(connection.pageId ? 1 : 0);
    const missing = connection.missingPermissions;
    const [isLoading, setIsLoading] = useState(false);

    const [pages, setPages] = useState<MetaPage[] | null>(null);
    const [page, setPage] = useState<MetaPage | null>(connection.pageId
        ? { id: connection.pageId, name: connection.pageName ?? connection.pageId }
        : null);

    const [forms, setForms] = useState<MetaForm[] | null>(null);
    const [form, setForm] = useState<MetaForm | null>(null);

    const [fields, setFields] = useState<MetaFormField[] | null>(null);

    // O formulário que já está valendo, para a lista mostrar qual é.
    const active = connection.formId;

    const [builderOpen, setBuilderOpen] = useState(false);

    /// Marca que o formulário nasceu aqui: só então faz sentido avisar que falta ligá-lo a um anúncio.
    const [justCreated, setJustCreated] = useState(false);

    const loadForms = async (pageId: string) => {
        setIsLoading(true);

        try {
            const res = await service.getForms(pageId);

            setForms(res.data.data ?? []);
        } catch (err) {
            setForms([]);
            showAlert(apiErrorMessage(err), 'error', 5000);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (connection.pageId) loadForms(connection.pageId);
    }, []);

    // A página só é buscada quando o passo dela abre: a chamada custa uma ida à Meta.
    useEffect(() => {
        if (step !== 0 || pages !== null) return;

        setIsLoading(true);
        service
            .getPages()
            .then((res) => setPages(res.data.data ?? []))
            .catch((err) => {
                setPages([]);
                showAlert(apiErrorMessage(err), 'error', 5000);
            })
            .finally(() => setIsLoading(false));
    }, [step]);

    const choosePage = async (chosen: MetaPage) => {
        setPage(chosen);
        setForms(null);
        setForm(null);
        setStep(1);

        await loadForms(chosen.id);
    };

    /// Formulário novo entra direto no passo dos campos, sem procurá-lo numa lista.
    const afterCreate = async (created: { id: string; name: string }) => {
        setBuilderOpen(false);
        setJustCreated(true);

        if (page) await loadForms(page.id);

        await chooseForm(
            { id: created.id, name: created.name, status: 'ACTIVE', leadsCount: 0 });
    };

    /// A Meta não apaga formulário de cadastro, só arquiva: some dos anúncios; os leads ficam.
    const archive = (target: MetaForm) => {
        if (!page) return;

        alertDelete(target.name, async () => {
            setLoading(dispatch, true);

            try {
                await service.archiveForm(page.id, target.id);
                await loadForms(page.id);
            } catch (err) {
                showAlert(apiErrorMessage(err), 'error', 8000);
            } finally {
                setLoading(dispatch, false);
            }
        }, t('leadForms.arquivarAviso'));
    };

    const chooseForm = async (chosen: MetaForm) => {
        setForm(chosen);
        setFields(null);
        setStep(2);
        setIsLoading(true);

        try {
            const res = await service.getFields(chosen.id);
            setFields(res.data.data ?? []);
        } catch (err) {
            setFields([]);
            showAlert(apiErrorMessage(err), 'error', 5000);
        } finally {
            setIsLoading(false);
        }
    };

    const complete = async () => {
        if (!page) return;

        setLoading(dispatch, true);

        try {
            await service.activate({
                pageId: page.id,
                pageName: page.name,
                formId: form?.id,
                formName: form?.name,
            });

            setStep(3);
            onSaved();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 6000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    const skeleton = (rows: number) => (
        <Box sx={{ display: 'grid', gap: 1 }}>
            {Array.from({ length: rows }, (_, i) => i).map((i) => (
                <Skeleton key={i} height={44} variant="rectangular" />
            ))}
        </Box>
    );

    return (
        <Box>
            <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
                {STEPS.map((name) => (
                    <Step key={name}>
                        <StepLabel>{t(`leadForms.passos.${name}`)}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {step === 0 && (
                <Box>
                    {missing.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="caption">
                                {t('leadForms.faltamPermissoes', { lista: missing.join(', ') })}
                            </Typography>
                        </Alert>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {t('leadForms.escolhaPagina')}
                    </Typography>
                    {isLoading || pages === null ? (
                        skeleton(3)
                    ) : pages.length === 0 ? (
                        <Alert severity="warning">{t('leadForms.semPaginas')}</Alert>
                    ) : (
                        <List dense>
                            {pages.map((item) => (
                                <ListItemButton key={item.id} onClick={() => choosePage(item)}>
                                    <ListItemText primary={item.name} secondary={item.id} />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </Box>
            )}

            {step === 1 && (
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {t('leadForms.escolhaFormulario', { pagina: page?.name ?? '' })}
                    </Typography>
                    {isLoading || forms === null ? (
                        skeleton(4)
                    ) : forms.length === 0 ? (
                        <Box>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="caption">
                                    {/* Lista vazia tem duas causas (página sem formulário, conta sem acesso); mesma ação. */}
                                    {missing.includes('pages_manage_ads')
                                        ? t('leadForms.paginaInteiraSemPermissao')
                                        : t('leadForms.paginaInteiraAjuda')}
                                </Typography>
                            </Alert>
                            <Box sx={{ textAlign: 'center' }}>
                                <MDButton variant="gradient" color="success" onClick={complete}>
                                    {t('leadForms.ativarPagina', { pagina: page?.name ?? '' })}
                                </MDButton>
                            </Box>
                        </Box>
                    ) : (
                        <List dense>
                            {forms.map((item) => (
                                <ListItemButton
                                    key={item.id}
                                    onClick={() => chooseForm(item)}
                                    disabled={item.status.toUpperCase() !== 'ACTIVE'}
                                >
                                    <ListItemText
                                        primary={item.name}
                                        secondary={t('leadForms.leadsCriados', { total: item.leadsCount })}
                                    />
                                    {item.id === active && (
                                        <Chip
                                            size="small"
                                            color="success"
                                            icon={<CheckCircleIcon />}
                                            label={t('leadForms.emUso')}
                                        />
                                    )}
                                    {item.status.toUpperCase() !== 'ACTIVE' && (
                                        <Chip size="small" label={item.status} />
                                    )}
                                    {/* O formulário em uso não some daqui sem escolher outro antes. */}
                                    {item.status.toUpperCase() === 'ACTIVE'
                                        && item.id !== active && (
                                        <Tooltip title={t('leadForms.arquivar')}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    archive(item);
                                                }}
                                            >
                                                <Inventory2OutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                    <Box
                        sx={{
                            mt: 2,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <MDButton
                            variant="outlined"
                            color="info"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => setBuilderOpen(true)}
                        >
                            {t('leadForms.criarFormulario')}
                        </MDButton>
                        <MDButton
                            variant="text"
                            color="info"
                            size="small"
                            onClick={() => setStep(0)}
                        >
                            {t('leadForms.trocarPagina')}
                        </MDButton>
                    </Box>
                </Box>
            )}

            {step === 2 && (
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {t('leadForms.camposAjuda')}
                    </Typography>
                    {isLoading || fields === null ? (
                        skeleton(4)
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('leadForms.campoNoFacebook')}</TableCell>
                                    <TableCell>{t('leadForms.campoNoCrm')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {fields.map((field) => (
                                    <TableRow key={field.key}>
                                        <TableCell>{field.label || field.key}</TableCell>
                                        <TableCell>{field.mappedTo}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            )}

            {step === 3 && (
                <Box sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {t('leadForms.prontoTitulo')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {form
                                ? t('leadForms.prontoAjuda', {
                                      formulario: form.name,
                                      pagina: page?.name ?? '',
                                  })
                                : t('leadForms.prontoPagina', { pagina: page?.name ?? '' })}
                        </Typography>
                    </Box>

                    {/* Formulário sozinho não coleta: quem coleta é o anúncio; sem aviso, a pessoa espera lead. */}
                    {justCreated && form && (
                        <Alert severity="info" icon={false}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
                            >
                                {t('leadForms.gerenciadorTitulo')}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ mb: 1.5 }}>
                                {t('leadForms.gerenciadorAjuda', {
                                    formulario: form.name,
                                })}
                            </Typography>
                            <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                endIcon={<OpenInNewIcon />}
                                onClick={() => window.open(
                                    'https://adsmanager.facebook.com/adsmanager',
                                    '_blank',
                                    'noopener')}
                            >
                                {t('leadForms.abrirGerenciador')}
                            </MDButton>
                        </Alert>
                    )}
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Box>
                    {step > 0 && step < 3 && (
                        <MDButton variant="gradient" color="dark" onClick={() => setStep(step - 1)}>
                            {t('leadForms.voltar')}
                        </MDButton>
                    )}
                </Box>
                <Box>
                    <MDButton
                        sx={{ mr: step === 2 ? 1 : 0 }}
                        variant="gradient"
                        color="dark"
                        onClick={onClose}
                    >
                        {step === 3 ? t('general.fechar') : t('general.cancelar')}
                    </MDButton>
                    {step === 2 && (
                        <MDButton variant="gradient" color="success" onClick={complete}>
                            {t('leadForms.ativar')}
                        </MDButton>
                    )}
                </Box>
            </Box>

            {page && (
                <LeadFormBuilder
                    open={builderOpen}
                    pageId={page.id}
                    pageName={page.name}
                    onClose={() => setBuilderOpen(false)}
                    onCreated={afterCreate}
                />
            )}
        </Box>
    );
};
